import { useFormaliteService } from '@europrocurement/l2d-domain';
import { castIriToId } from '@europrocurement/l2d-utils';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { DataProviderProps, DataState } from './types';

type Formality = {
    id: number;
    label: string;
    sortOrder: number;
    formId?: number;
};

type FormalityState = DataState & {
    data: Array<Formality>;
};

const INIT_ITEMS_CTX = 'init_formalities_ctx';

type Action = { type: 'init_formalities_ctx'; payload: Array<Formality> };

export const FormalitiesContext = createContext<FormalityState | null>(null);
export const FormalitiesDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function formalitiesReducer(state: FormalityState, action: Action) {
    switch (action.type) {
        case INIT_ITEMS_CTX: {
            return {
                loaded: true,
                data: [...action.payload],
            };
        }
        default: {
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

export const FormalitiesProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [state, dispatch] = useReducer(formalitiesReducer, { loaded: false, data: [] });
        const { formaliteApi } = useFormaliteService();

        useEffect(() => {
            if (!autoload) {
                return;
            }

            formaliteApi
                .apiFormalitesGetCollection({ itemsPerPage: 999 })
                .then((response) => {
                    dispatch({
                        type: INIT_ITEMS_CTX,
                        payload: response.data['hydra:member'].map((formality) => ({
                            id: formality.id as number,
                            label: formality.libelle as string,
                            sortOrder: formality.rang as number,
                            formId: formality.formulaire
                                ? castIriToId(formality.formulaire)
                                : undefined,
                        })),
                    });
                })
                .catch((e) => console.error(e));
        }, [formaliteApi, autoload]);

        return (
            <FormalitiesContext.Provider value={state}>
                <FormalitiesDispatchContext.Provider value={dispatch}>
                    {children}
                </FormalitiesDispatchContext.Provider>
            </FormalitiesContext.Provider>
        );
    };

export function useFormalitiesCtx() {
    const state = useContext(FormalitiesContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getFormalities = (): Array<Formality> => state?.data || [];

    const getFormalityById = (id: number): Formality | undefined =>
        (state?.data || []).find((formality) => formality.id === id);

    const getFormalitiesFiltered = (
        filters: Partial<Record<keyof Formality, unknown>>,
    ): Array<Formality> =>
        (state?.data || []).filter((item) =>
            Object.entries(filters).every(([key, value]) => item[key as keyof Formality] === value),
        );

    const getFormalityFiltered = (
        filters: Partial<Record<keyof Formality, unknown>>,
    ): Formality | undefined => getFormalitiesFiltered(filters)[0] || undefined;

    return {
        isDataLoaded,
        getFormalities,
        getFormalityById,
        getFormalitiesFiltered,
        getFormalityFiltered,
    };
}

export function useFormalitiesCtxDispatch() {
    return useContext(FormalitiesDispatchContext);
}
